import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

export interface BadgeProps {
  tone?: BadgeTone
  children: ReactNode
  /** A leading dot, for a status that reads faster as a shape than as a word. */
  hasDot?: boolean
  className?: string
}

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-text-secondary border-border',
  primary: 'bg-primary-subtle text-primary border-primary/25',
  success: 'bg-success-subtle text-success border-success/25',
  warning: 'bg-warning-subtle text-warning border-warning/25',
  danger: 'bg-danger-subtle text-danger border-danger/25',
  info: 'bg-info-subtle text-info border-info/25',
}

/**
 * Colour is never the only carrier of meaning here — the badge always contains its own text,
 * so it reads correctly in greyscale and to a screen reader.
 */
export function Badge({ tone = 'neutral', children, hasDot = false, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
        'text-[0.6875rem] leading-4 font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {hasDot ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null}
      {children}
    </span>
  )
}
